import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import openPhoneService from '../services/openPhoneService';

// Create context
const PhoneServiceContext = createContext();

// Phone service options
export const PHONE_SERVICES = {
  GOOGLE_VOICE: 'googleVoice',
  OPENPHONE: 'openPhone'
};

// Rate limiting configuration
const LINK_BATCH_INTERVAL = 3000; // Process link generation in batches every 3 seconds
const MAX_CONCURRENT_REQUESTS = 3; // Only process up to 3 link requests at a time

export const PhoneServiceProvider = ({ children }) => {
  // Default to OpenPhone
  const [activeService, setActiveService] = useState(PHONE_SERVICES.OPENPHONE);
  const [isOpenPhoneConfigured, setIsOpenPhoneConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [phoneLinks, setPhoneLinks] = useState({}); // Cache for generated links
  
  // Refs to track pending operations and prevent duplicate requests
  const pendingLinkRequests = useRef(new Map());
  const initialLoadComplete = useRef(false);
  const linkBatchTimeout = useRef(null);
  const isProcessingBatch = useRef(false);

  // Check if OpenPhone is configured on mount
  useEffect(() => {
    const checkOpenPhoneConfig = async () => {
      // Only run this once
      if (initialLoadComplete.current) return;
      
      try {
        setIsLoading(true);
        const configured = await openPhoneService.isConfigured();
        console.log('OpenPhone configuration check:', { configured, SERVICES: PHONE_SERVICES });
        setIsOpenPhoneConfigured(configured);
        
        // No need to pre-fetch contacts on initial load
        // We'll fetch them lazily as needed instead
        initialLoadComplete.current = true;
      } catch (error) {
        console.error('Error checking OpenPhone configuration:', error);
        setIsOpenPhoneConfigured(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkOpenPhoneConfig();
    
    // Set up periodic processing of link requests
    startLinkBatchProcessing();
    
    return () => {
      // Clean up on unmount
      if (linkBatchTimeout.current) {
        clearTimeout(linkBatchTimeout.current);
      }
    };
  }, []);
  
  // Start the batch processing of link requests
  const startLinkBatchProcessing = () => {
    if (linkBatchTimeout.current) {
      clearTimeout(linkBatchTimeout.current);
    }
    
    linkBatchTimeout.current = setTimeout(processLinkBatch, LINK_BATCH_INTERVAL);
  };
  
  // Process a batch of link requests
  const processLinkBatch = async () => {
    if (isProcessingBatch.current || pendingLinkRequests.current.size === 0) {
      // Already processing or nothing to process
      startLinkBatchProcessing();
      return;
    }
    
    isProcessingBatch.current = true;
    
    try {
      // Convert pending requests to array for processing
      const requests = Array.from(pendingLinkRequests.current.entries());
      
      // Process a limited number of requests to avoid rate limiting
      const batch = requests.slice(0, MAX_CONCURRENT_REQUESTS);
      
      // Remove the batched requests from pending map
      batch.forEach(([key]) => {
        pendingLinkRequests.current.delete(key);
      });
      
      // Process each request
      const results = await Promise.all(
        batch.map(async ([key, { phoneNumber, action }]) => {
          try {
            let result = null;
            if (action === 'call') {
              result = await openPhoneService.generateCallLink(phoneNumber);
            } else if (action === 'text') {
              result = await openPhoneService.generateTextLink(phoneNumber);
            }
            
            return { key, result };
          } catch (error) {
            console.error(`Error generating ${action} link for ${phoneNumber}:`, error);
            return { key, result: null };
          }
        })
      );
      
      // Update the links state with results
      const newLinks = {};
      results.forEach(({ key, result }) => {
        if (result) {
          newLinks[key] = result;
        }
      });
      
      if (Object.keys(newLinks).length > 0) {
        setPhoneLinks(prev => ({
          ...prev,
          ...newLinks
        }));
      }
    } catch (error) {
      console.error('Error processing link batch:', error);
    } finally {
      isProcessingBatch.current = false;
      startLinkBatchProcessing();
    }
  };

  // Toggle between phone services
  const togglePhoneService = () => {
    console.log('Toggling service from', activeService, 'to', 
      activeService === PHONE_SERVICES.GOOGLE_VOICE 
        ? PHONE_SERVICES.OPENPHONE 
        : PHONE_SERVICES.GOOGLE_VOICE
    );
    setActiveService(prev => 
      prev === PHONE_SERVICES.GOOGLE_VOICE 
        ? PHONE_SERVICES.OPENPHONE 
        : PHONE_SERVICES.GOOGLE_VOICE
    );
  };

  // Check if a service is active
  const isServiceActive = (service) => activeService === service;

  // Queue a link generation request to be processed in batch
  const queueLinkGeneration = (phoneNumber, action) => {
    const cacheKey = `${phoneNumber}_${action}`;
    
    // Add to pending requests if not already there
    if (!pendingLinkRequests.current.has(cacheKey)) {
      pendingLinkRequests.current.set(cacheKey, { phoneNumber, action });
    }
  };

  // Generate appropriate phone URL based on active service
  // This sync function returns a URL immediately but may update it later async for OpenPhone
  const generatePhoneUrl = (phoneNumber, action = 'call') => {
    if (!phoneNumber) return null;
    
    // Clean the phone number
    const formattedPhone = phoneNumber.replace(/\D/g, '');
    if (!formattedPhone) return null;

    // Format for Google Voice
    const googleVoiceNumber = formattedPhone.startsWith('1') 
      ? formattedPhone 
      : `1${formattedPhone}`;
    
    if (activeService === PHONE_SERVICES.GOOGLE_VOICE) {
      // Google Voice URLs
      if (action === 'call') {
        return `https://voice.google.com/u/0/calls?a=nc,%2B${googleVoiceNumber}`;
      } else if (action === 'text') {
        return `https://voice.google.com/u/0/messages/new?a=nc,%2B${googleVoiceNumber}`;
      }
    } else if (isOpenPhoneConfigured) {
      // For OpenPhone, check cached links first or return inbox URL and update async
      const cacheKey = `${phoneNumber}_${action}`;
      
      // If we have a cached link, use it
      if (phoneLinks[cacheKey]) {
        return phoneLinks[cacheKey];
      }
      
      // Otherwise, return inbox as fallback and queue async update
      const fallbackUrl = `https://my.openphone.com/inbox`;
      
      // Queue update for batch processing
      queueLinkGeneration(phoneNumber, action);
      
      return fallbackUrl;
    }
    
    return null;
  };

  // Add a contact to OpenPhone specifically
  const addToOpenPhone = async (lead, retryCount = 0) => {
    if (!isOpenPhoneConfigured) {
      console.error('OpenPhone is not configured');
      return null;
    }
    
    try {
      // Create a properly formatted contact object for OpenPhone service
      const contactData = {
        firstName: lead.first_name || '',
        lastName: lead.last_name || '',
        phoneNumber: lead.phone, // Map lead.phone to phoneNumber property
        email: lead.email || '',
        company: lead.company || ''
      };
      
      // Validate required phone number
      if (!contactData.phoneNumber) {
        throw new Error('Phone number is required to create a contact');
      }
      
      console.log(`Attempting to create OpenPhone contact (attempt ${retryCount + 1})`, contactData);
      
      try {
        const result = await openPhoneService.createContact(contactData);
        
        // Refresh the contact links for this phone number
        if (result && result.id && lead.phone) {
          const phone = lead.phone;
          const contactUrl = openPhoneService.generateContactUrl(result.id);
          
          // Update cached links
          setPhoneLinks(prev => ({
            ...prev,
            [`${phone}_call`]: contactUrl,
            [`${phone}_text`]: contactUrl
          }));
        }
        
        return result;
      } catch (error) {
        // If the error is a rate limit and we've not retried too many times, wait and retry
        if (error.message.includes('Rate limit') && retryCount < 2) {
          console.log(`Rate limit hit, retrying in ${(retryCount + 1) * 3} seconds...`);
          
          // Wait with increasing backoff
          await new Promise(resolve => 
            setTimeout(resolve, (retryCount + 1) * 3000));
          
          // Retry with incrementing the retry counter
          return addToOpenPhone(lead, retryCount + 1);
        }
        
        // Otherwise, propagate the error
        throw error;
      }
    } catch (error) {
      console.error('Error adding contact to OpenPhone:', error);
      throw error;
    }
  };

  const value = {
    activeService,
    isOpenPhoneConfigured,
    isLoading,
    togglePhoneService,
    isServiceActive,
    generatePhoneUrl,
    addToOpenPhone,
    PHONE_SERVICES
  };

  return (
    <PhoneServiceContext.Provider value={value}>
      {children}
    </PhoneServiceContext.Provider>
  );
};

// Custom hook to use the phone service context
export const usePhoneService = () => {
  const context = useContext(PhoneServiceContext);
  if (context === undefined) {
    throw new Error('usePhoneService must be used within a PhoneServiceProvider');
  }
  return context;
};

export default PhoneServiceContext; 